'use client'

import type { Entry, TopicRef } from '@/document'
import type { BookStatus } from './meta'
import { cn } from '@/lib/tailwind'
import {
  BookOpenIcon,
  CheckIcon,
  BookBookmarkIcon,
  ClockIcon,
  StarIcon,
} from '@phosphor-icons/react'
import Image from 'next/image'

interface BookCardProps {
  book: Entry & { topics?: TopicRef[] }
  className?: string
}

const STATUS_ICONS = {
  read: CheckIcon,
  reading: BookBookmarkIcon,
  want_to_read: ClockIcon,
} as const

export function BookCard({ book, className }: BookCardProps) {
  const metadata = book.metadata as {
    coverUrl?: string
    author?: string
    status?: BookStatus
    rating?: number
  }
  const topics = book.topics || []
  const hasCover = !!book.imageUrl
  const statusKey = (metadata.status ||
    'want_to_read') as keyof typeof STATUS_ICONS
  const StatusIcon = STATUS_ICONS[statusKey] || ClockIcon

  return (
    <div
      className={cn(
        'group hover:border-foreground/20 relative overflow-hidden rounded-lg border transition-colors',
        className,
      )}
    >
      {/* Cover / Placeholder */}
      <div className="bg-muted relative aspect-[2/3] overflow-hidden">
        {hasCover ? (
          <Image
            src={book.imageUrl!}
            alt={book.title}
            fill
            className="object-cover transition-transform group-hover:scale-105"
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <BookOpenIcon className="text-muted-foreground/30 h-12 w-12" />
          </div>
        )}

        {/* Rating badge (subtle, monochrome) */}
        {metadata.rating && (
          <div className="absolute bottom-2 left-2">
            <span className="flex items-center gap-0.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white/90 backdrop-blur-sm">
              <StarIcon className="h-2.5 w-2.5" weight="fill" />
              {metadata.rating}
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="space-y-0.5 p-2.5">
        <div className="flex items-start justify-between gap-1.5">
          <h3 className="line-clamp-2 text-sm leading-tight font-medium">
            {book.title}
          </h3>
          {/* Subtle status icon */}
          <StatusIcon
            className={cn(
              'mt-0.5 h-3 w-3 shrink-0',
              metadata.status === 'read'
                ? 'text-muted-foreground'
                : metadata.status === 'reading'
                  ? 'text-foreground/70'
                  : 'text-muted-foreground/60',
            )}
            weight={metadata.status === 'reading' ? 'fill' : 'bold'}
          />
        </div>
        {metadata.author && (
          <p className="text-muted-foreground line-clamp-1 text-[11px]">
            {metadata.author}
          </p>
        )}
        {/* Topics from resolved topicIds */}
        {topics.length > 0 && (
          <p className="text-muted-foreground/60 line-clamp-1 text-[11px]">
            {topics
              .slice(0, 3)
              .map((t) => t.name)
              .join(' · ')}
          </p>
        )}
      </div>
    </div>
  )
}
